"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Game = void 0;
const rxjs_1 = require("rxjs");
const pixi_js_1 = require("pixi.js");
const Global_1 = require("./Global");
const fontfaceobserver_1 = __importDefault(require("fontfaceobserver"));
const blocks_1 = require("./blocks");
const config_json_1 = __importDefault(require("../../shared/config.json"));
const i18next_1 = __importDefault(require("i18next"));
const AssetManager_1 = __importDefault(require("./managers/AssetManager"));
const dump_svg_1 = __importDefault(require("../assets/svg/dump.svg"));
const SceneManager_1 = require("./managers/SceneManager");
const GameManager_1 = require("./managers/GameManager");
const interfaces_1 = require("../../shared/interfaces");
const ConfirmPopup_1 = require("./popups/ConfirmPopup");
const ConnectingPopup_1 = require("./popups/ConnectingPopup");
const WindowManager_1 = require("./managers/WindowManager");
const i18next_browser_languagedetector_1 = __importDefault(require("i18next-browser-languagedetector"));
const Navbar_1 = require("./blocks/Navbar");
const settings_1 = require("./config/settings");
const colors_1 = require("./config/colors");
const ColorAdapter_1 = require("./config/ColorAdapter");
const RedirectPopup_1 = require("./popups/RedirectPopup");
const MenuScene_1 = require("./scenes/MenuScene");
class Game {
    constructor() {
        this.started = false;
        this.sigs = [];
        this.ignore = (e) => {
            e.preventDefault();
        };
    }
    init(into = document.body) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.destroy();
            this.into = into;
            this.gameManager = yield (new GameManager_1.GameManager()).init();
            // Hide our status bar (it's a video game so don't need it)
            (_a = window.StatusBar) === null || _a === void 0 ? void 0 : _a.hide();
            const eventParams = { passive: false };
            this.into.addEventListener('touchcancel', this.ignore, eventParams);
            this.into.addEventListener('touchend', this.ignore, eventParams);
            this.subs = new rxjs_1.Subscription();
            this.app = new pixi_js_1.Application({
                width: (0, Global_1.px)(window.innerWidth),
                height: (0, Global_1.px)(window.innerHeight),
                background: settings_1.Settings.background,
                antialias: false,
                powerPreference: "high-performance"
            });
            this.app.stage.sortableChildren = true;
            into.appendChild((this.app.view));
            // Load Necessary Fonts, Assets, Translations, etc.
            yield Promise.all([this.loadFonts(), this.loadTranslation(), this.loadAssets()]);
            this.windowManager = new WindowManager_1.WindowManager((this.app.view));
            // Resize Event
            this.subs.add((0, rxjs_1.fromEvent)(window, 'resize')
                .pipe((0, rxjs_1.debounce)(() => (0, rxjs_1.interval)(200))).subscribe({
                next: (data) => __awaiter(this, void 0, void 0, function* () {
                    yield this.resize();
                })
            }));
            this.subs.add((0, rxjs_1.fromEvent)(window, 'orientationchange').subscribe(() => __awaiter(this, void 0, void 0, function* () {
                yield this.resize();
            })));
            this.subs.add(this.gameManager.adManager.interstitialEnd$.subscribe(() => {
                // TODO: Check this with apps
                // setTimeout(async () => await this.resize(), 500);
            }));
            // Scene Manager
            this.sceneManager = new SceneManager_1.SceneManager(this.app, this.gameManager);
            // Change assetmanager in music
            this.subs.add(this.gameManager.playerManager.options$.subscribe((ops) => __awaiter(this, void 0, void 0, function* () {
                AssetManager_1.default.soundsOn = this.gameManager.playerManager.options$.value.sound;
                AssetManager_1.default.backgroundOn = ops.music;
            })));
            // Refresh if our lang is changed
            this.subs.add(this.gameManager.langChange$.subscribe((newlang) => __awaiter(this, void 0, void 0, function* () {
                if (newlang && newlang !== i18next_1.default.resolvedLanguage) {
                    yield i18next_1.default.changeLanguage(newlang);
                    // await this.sceneManager.routeTo(Scenes.Home, null, true);
                }
            })));
            this.subs.add(this.gameManager.roomStream.draw$.subscribe((draw) => __awaiter(this, void 0, void 0, function* () {
                const room = this.gameManager.room$.value;
                if ((draw === null || draw === void 0 ? void 0 : draw.state) && room) {
                    yield this.sceneManager.routeTo(room.type === interfaces_1.GameType.Guess
                        || room.type === interfaces_1.GameType.GuessFriends ? SceneManager_1.Scenes.Guesser : SceneManager_1.Scenes.Draw, null, document.visibilityState === 'hidden');
                }
            })));
            this.subs.add(this.gameManager.roomStream.round$.pipe((0, rxjs_1.skip)(1)).subscribe({
                next: (round) => __awaiter(this, void 0, void 0, function* () {
                    if ((round === null || round === void 0 ? void 0 : round.start) && this.gameManager.room$.value) {
                        yield this.sceneManager.routeTo(SceneManager_1.Scenes.Round, null, document.visibilityState === 'hidden');
                    }
                })
            }));
            this.subs.add(this.gameManager.roomStream.complete$.subscribe({
                next: (room) => __awaiter(this, void 0, void 0, function* () {
                    var _b;
                    if (room.rkey === ((_b = this.gameManager.room$.value) === null || _b === void 0 ? void 0 : _b.rkey)) {
                        yield this.sceneManager.routeTo(SceneManager_1.Scenes.Complete, null, document.visibilityState === 'hidden');
                    }
                })
            }));
            this.subs.add(this.gameManager.room$.subscribe({
                next: (room) => __awaiter(this, void 0, void 0, function* () {
                    // Remove popup if we lose our room
                    if (!room && this.confirmPopup && !this.confirmPopup.destroyed) {
                        this.confirmPopup.destroy(true);
                        this.confirmPopup = null;
                    }
                })
            }));
            // In case we do not recover in time...
            this.subs.add(this.gameManager.crash$.subscribe(() => __awaiter(this, void 0, void 0, function* () {
                const cur = this.sceneManager.currentScene;
                if (cur.name !== SceneManager_1.Scenes.Home && !cur.isDeinited) {
                    yield cur.crashScene();
                }
            })));
            // Add the header
            this.header = this.makeHeader();
            this.navbar = this.makeNavbar();
            if ((window === null || window === void 0 ? void 0 : window.location.pathname) === '/logo') {
                this.header.visible = this.navbar.visible = false;
                yield this.sceneManager.routeTo(SceneManager_1.Scenes.Logo);
            }
            else {
                const hbutton = this.header.menu;
                const bbutton = this.header.title;
                const sbutton = this.header.brushButton;
                const rbutton = this.header.streakButton;
                const gohome = (target, to) => __awaiter(this, void 0, void 0, function* () {
                    var _c, _d;
                    if (to === void 0) { to = ((_d = (_c = this.sceneManager.currentScene) === null || _c === void 0 ? void 0 : _c.backTo()) !== null && _d !== void 0 ? _d : SceneManager_1.Scenes.Home); }
                    if (this.confirmPopup) {
                        this.confirmPopup.destroy(true);
                        this.confirmPopup = null;
                    }
                    const current = this.gameManager.room$.value;
                    if (current && (((current.type === interfaces_1.GameType.Battle
                        || current.type === interfaces_1.GameType.Guess
                        || current.type === interfaces_1.GameType.GuessFriends
                        || current.type === interfaces_1.GameType.BattleFriends) && current.num_players > 1)
                        || current.type === interfaces_1.GameType.Speed
                        || current.type === interfaces_1.GameType.Tutorial)
                        && !current.open
                        && !current.complete) {
                        this.confirmPopup = this.makeConfirmLeavePopup(current.type);
                        this.layout();
                        AssetManager_1.default.untickSound();
                    }
                    else {
                        if (to !== this.sceneManager.currentScene.name) {
                            AssetManager_1.default.untickSound();
                        }
                        yield this.sceneManager.routeTo(to, target);
                    }
                });
                this.sigs.push(hbutton.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
                    yield gohome(hbutton);
                })));
                this.sigs.push(bbutton.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
                    yield gohome(bbutton, SceneManager_1.Scenes.Home);
                })));
                let since = null;
                this.subs.add(this.sceneManager.sceneDidChange$.subscribe((cur) => __awaiter(this, void 0, void 0, function* () {
                    this.layout();
                    if ((0, Global_1.isWeb)() && (0, Global_1.isProd)() && (cur instanceof MenuScene_1.MenuScene) && !this.redirectPopup
                        && (!since || since < (Date.now() - config_json_1.default.adinterval))) {
                        this.redirectPopup = this.makeAppRedirectPopup();
                        this.redirectPopup.cancel = this.redirectPopup.confirm = () => __awaiter(this, void 0, void 0, function* () {
                            this.redirectPopup = null;
                            since = Date.now();
                        });
                        this.layout();
                        this.layout();
                    }
                    if (cur && hbutton && sbutton && rbutton) {
                        if (cur.zIndex === SceneManager_1.Layer.Menu) {
                            yield Promise.all([(0, Global_1.fade)(hbutton, 0.0), (0, Global_1.fade)(sbutton, 1.0), (0, Global_1.fade)(rbutton, 1.0)]);
                        }
                        else if (cur.name === SceneManager_1.Scenes.Complete) {
                            yield Promise.all([(0, Global_1.fade)(hbutton, 1.0), (0, Global_1.fade)(sbutton, 1.0), (0, Global_1.fade)(rbutton, 1.0)]);
                        }
                        else {
                            yield (0, Global_1.fade)(hbutton, 1.0);
                        }
                    }
                })));
                this.subs.add(this.sceneManager.sceneWillChange$.subscribe((cur) => __awaiter(this, void 0, void 0, function* () {
                    if (cur) {
                        cur.layout();
                        this.navbar.visible = (cur.name !== SceneManager_1.Scenes.Tutorial);
                        this.navbar.enabled = (cur.zIndex === SceneManager_1.Layer.Menu);
                        if (cur.name === SceneManager_1.Scenes.Tutorial) {
                            bbutton.visible = false;
                        }
                        if (hbutton && cur.name === SceneManager_1.Scenes.Home) {
                            yield (0, Global_1.fade)(hbutton, 0.0, 0);
                        }
                        if (sbutton && rbutton && cur.name !== SceneManager_1.Scenes.Stats && cur.name !== SceneManager_1.Scenes.Home && cur.name !== SceneManager_1.Scenes.Complete) {
                            yield Promise.all([(0, Global_1.fade)(sbutton, 0.0), (0, Global_1.fade)(rbutton, 0.0)]);
                        }
                    }
                })));
            }
            // Popup sub and volume for connection/focus errors
            let wasplaying = false;
            this.subs.add(this.gameManager.connected$.subscribe((isconnected) => __awaiter(this, void 0, void 0, function* () {
                var _e;
                (_e = this.connectPopup) === null || _e === void 0 ? void 0 : _e.destroy(true);
                this.connectPopup = null;
                if (!isconnected) {
                    // Mute the volume
                    yield AssetManager_1.default.masterSound(false);
                    this.connectPopup = this.makeConnectingPopup();
                    this.layout();
                }
                else {
                    yield AssetManager_1.default.masterSound(true);
                }
            })));
            return this;
        });
    }
    handleInvite() {
        var _a, _b;
        const invited = (_b = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.hash) === null || _b === void 0 ? void 0 : _b.split('#')[1];
        if ((invited === null || invited === void 0 ? void 0 : invited.length) === 6) {
            this.gameManager.enterRoom(interfaces_1.GameType.BattleFriends, invited);
            return true;
        }
        return false;
    }
    loadTranslation() {
        return __awaiter(this, void 0, void 0, function* () {
            const langs = {};
            config_json_1.default.langs.forEach((lang) => {
                langs[lang] = {
                    translation: require('../locales/' + lang + '.json')
                };
            });
            yield i18next_1.default.use(i18next_browser_languagedetector_1.default).init({
                fallbackLng: false,
                fallbackNS: 'translation',
                defaultNS: 'translation',
                debug: ['prod', 'production'].indexOf(process.env.NODE_ENV) === -1,
                resources: langs,
                supportedLngs: config_json_1.default.langs,
                detection: {
                    convertDetectedLanguage: (lng) => {
                        if (config_json_1.default.langs.indexOf(lng) !== -1) {
                            return lng;
                        }
                        return lng.replace(/-.*/, '');
                    }
                }
            });
            return i18next_1.default;
        });
    }
    loadAssets() {
        return __awaiter(this, void 0, void 0, function* () {
            yield AssetManager_1.default.loadAll();
            // Cache all adapters (this will just happen in the constructor, so just make one and then destory it)
            const promises = [];
            for (let i in colors_1.colors) {
                promises.push(ColorAdapter_1.ColorAdapter.cache(this.app, colors_1.colors[i]));
            }
            yield Promise.all(promises);
        });
    }
    loadFonts() {
        return __awaiter(this, void 0, void 0, function* () {
            const font = new fontfaceobserver_1.default(settings_1.Settings.font, {});
            return yield font.load();
        });
    }
    resize() {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            yield this.detectInsets();
            const width = ((_a = this.into) === null || _a === void 0 ? void 0 : _a.clientWidth) || window.innerWidth;
            const height = ((_b = this.into) === null || _b === void 0 ? void 0 : _b.clientHeight) || window.innerHeight;
            const newWidth = (0, Global_1.px)(width);
            const newHeight = (0, Global_1.px)(height);
            this.app.renderer.resize(newWidth, newHeight);
            ((_c = this.app) === null || _c === void 0 ? void 0 : _c.view).style.width = width + 'px';
            ((_d = this.app) === null || _d === void 0 ? void 0 : _d.view).style.height = height + 'px';
            // Layout Children
            this.layout();
            return { width, height };
        });
    }
    layout() {
        var _a, _b;
        this.app.stage.children.forEach((child) => {
            if (typeof child.layout === 'function') {
                child.layout();
            }
        });
        const landscape = (_a = this.navbar) === null || _a === void 0 ? void 0 : _a.isLandscapeBox;
        (_b = this.navbar) === null || _b === void 0 ? void 0 : _b.position.set(0, landscape ? (0, Global_1.px)(-this.navbar.border)
            : (this.app.screen.height - this.navbar.height + (0, Global_1.px)(this.navbar.border)));
    }
    lockOrientation() {
        if ((0, Global_1.isDevice)()) {
            window.screen.orientation.lock('portrait');
        }
        else {
        }
    }
    makeHeader() {
        const header = new blocks_1.Header(this.app, this.gameManager, this.sceneManager);
        return this.app.stage.addChild(header);
    }
    makeNavbar() {
        const nav = new Navbar_1.Navbar(this.app, this.gameManager, this.sceneManager);
        return this.app.stage.addChild(nav);
    }
    makeAppRedirectPopup() {
        const popup = new RedirectPopup_1.RedirectPopup(this.app);
        return this.app.stage.addChild(popup);
    }
    makeConfirmLeavePopup(type) {
        const sprite = pixi_js_1.Sprite.from(dump_svg_1.default);
        sprite.scale.set((0, Global_1.px)((this.app.screen.height / (0, Global_1.px)()) >= Global_1.BreakPoint.Small ? 0.3 : 0.25));
        let t = 'battle.confirm';
        if (type === interfaces_1.GameType.Speed) {
            t = 'speed.confirm';
        }
        else if (type === interfaces_1.GameType.Tutorial) {
            t = 'tutorial.confirm';
        }
        const popup = new ConfirmPopup_1.ConfirmPopup(this.app, i18next_1.default.t(t), i18next_1.default.t('stay'), i18next_1.default.t('leave'), sprite);
        this.gameManager.stopPlay();
        popup.cancel = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            this.confirmPopup = null;
            yield this.sceneManager.routeTo((_b = (_a = this.sceneManager.currentScene) === null || _a === void 0 ? void 0 : _a.backTo()) !== null && _b !== void 0 ? _b : SceneManager_1.Scenes.Home, this.header.menu);
        });
        popup.confirm = () => {
            this.gameManager.startPlay();
            this.confirmPopup = null;
        };
        return this.app.stage.addChild(popup);
    }
    makeConnectingPopup() {
        const popup = new ConnectingPopup_1.ConnectingPopup(this.app);
        return this.app.stage.addChild(popup);
    }
    detectInsets() {
        return __awaiter(this, void 0, void 0, function* () {
            // This plugin only works with Android
            const notch = window.AndroidNotch;
            if (notch && (0, Global_1.isAndroid)()) {
                const style = document.documentElement.style;
                // Apply insets as css variables
                const p1 = new Promise((resolve) => {
                    notch.getInsetTop((px) => {
                        style.setProperty("--notch-inset-top", px + "px");
                        resolve(px);
                    }, () => resolve(0));
                });
                const p2 = new Promise((resolve) => {
                    notch.getInsetRight((px) => {
                        style.setProperty("--notch-inset-right", px + "px");
                        resolve(px);
                    }, () => resolve(0));
                });
                const p3 = new Promise((resolve) => {
                    notch.getInsetBottom((px) => {
                        style.setProperty("--notch-inset-bottom", px + "px");
                        resolve(px);
                    }, () => resolve(0));
                });
                const p4 = new Promise((resolve) => {
                    notch.getInsetLeft((px) => {
                        style.setProperty("--notch-inset-left", px + "px");
                        resolve(px);
                    }, () => resolve(0));
                });
                yield Promise.all([p1, p2, p3, p4]);
            }
        });
    }
    destroy() {
        var _a, _b, _c;
        (_a = this.into) === null || _a === void 0 ? void 0 : _a.removeEventListener('touchcancel', this.ignore);
        (_b = this.into) === null || _b === void 0 ? void 0 : _b.removeEventListener('touchend', this.ignore);
        (_c = this.windowManager) === null || _c === void 0 ? void 0 : _c.destroy();
        if (this.app) {
            this.app.destroy(true);
            this.app = null;
        }
        if (this.gameManager) {
            this.gameManager.destroy();
            this.gameManager = null;
        }
        if (this.subs) {
            this.subs.unsubscribe();
            this.subs = null;
        }
        this.sigs.forEach((s) => {
            s === null || s === void 0 ? void 0 : s.disconnect();
        });
    }
    start() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.started) {
                this.started = true;
                // We wait here for the first value where we know about the player
                const newplayer = yield (0, rxjs_1.firstValueFrom)(this.gameManager.playerManager.firsttime$.pipe((0, rxjs_1.filter)(f => f !== null)));
                // Sent us straight into a room if we came from an invitation link
                const invited = this.handleInvite();
                if (invited) {
                    return yield this.sceneManager.routeTo(SceneManager_1.Scenes.PrivateBattle);
                }
                else if (newplayer) {
                    return yield this.sceneManager.routeTo(SceneManager_1.Scenes.Tutorial);
                }
                return yield this.sceneManager.routeTo(SceneManager_1.Scenes.Home);
            }
            return null;
        });
    }
}
exports.Game = Game;
